import { flexRender, useTable } from "@tanstack/react-table";
import type {
  ColumnDef,
  OnChangeFn,
  PaginationState,
  RowData,
  SortingState,
} from "@tanstack/react-table";
import { useVirtualizer } from "@tanstack/react-virtual";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { twMerge } from "tailwind-merge";
import { Icon } from "../Icon";
import type { TablePaginationProps } from "./pagination";
import { TablePaginationControls } from "./TablePaginationControls";
import { sellarTableFeatures, type SellarTableFeatures } from "./features";
import {
  TableBody,
  TableCell,
  TableContainer,
  TableEmptyState,
  TableHeader,
  TableHeaderCell,
  TableHeaderRow,
  TableLayout,
  TableRow,
} from "./TablePrimitives";

type InfiniteQueryType<T> = {
  data?: T[];
  fetchNextPage?: () => void;
  isFetching?: boolean;
  totalCount?: number;
};

const DEFAULT_PAGE_SIZE_OPTIONS = [25, 50, 75, 100];

export type TableProps<T extends RowData> = {
  columns: ColumnDef<SellarTableFeatures, T, any>[];
  data: T[];
  isPrinting?: boolean;
  infinite?: boolean;
  infiniteQuery?: InfiniteQueryType<T>;
  initialOffset?: number;
  setScrollOffset?: (offset: number) => void;
  clientSort?: boolean;
  onRowClick?: (row: T) => void;
  horizontalInfinite?: boolean;
  noResultsText?: string;
  loadingText?: string;
  paginationLabel?: string;
  gridClasses: string;
  gridStyles?: React.CSSProperties;
  columnSortName?: any;
  handleTableSort?: (value: any | null) => void;
  roundedBottom?: boolean;
  roundedTop?: boolean;
  pageSizeOptions?: number[];
  defaultPageSize?: number;
  /**
   * When true, the table is a single scroll surface: body scrolls inside a
   * viewport-fit shell, column headers stay sticky, and pagination stays pinned
   * under the body. Prefer this for desktop list UIs.
   *
   * Defaults to true for infinite and paginated tables.
   */
  containedScroll?: boolean;
} & TablePaginationProps;

export function Table<T extends RowData>({
  columns,
  data,
  isPrinting = false,
  infinite = false,
  infiniteQuery,
  initialOffset = 0,
  setScrollOffset,
  pageCount,
  pagination,
  setPagination,
  clientSort = false,
  clientPagination = false,
  onRowClick,
  horizontalInfinite = false,
  noResultsText = "Your search returned 0 results",
  loadingText = "Loading...",
  paginationLabel = "rows per page",
  gridClasses,
  gridStyles,
  columnSortName,
  handleTableSort,
  roundedBottom = true,
  roundedTop = false,
  pageSizeOptions = DEFAULT_PAGE_SIZE_OPTIONS,
  defaultPageSize = 25,
  containedScroll,
}: TableProps<T>) {
  const cursorPagination =
    pagination?.mode === "cursor" ? pagination : undefined;
  const offsetPagination =
    pagination?.mode === "cursor" ? undefined : pagination;
  const hasPagination =
    Boolean(cursorPagination) ||
    clientPagination ||
    Boolean(offsetPagination && setPagination);
  // One scroll surface for list tables (infinite + paginated). Opt out only for
  // short embedded tables that should flow with the page.
  const useContainedScroll =
    !isPrinting && (containedScroll ?? (infinite || hasPagination));
  // ----- Sorting state -----
  const [sorting, setSorting] = useState<SortingState>(
    columnSortName
      ? [
          {
            id: columnSortName.replace("_asc", "").replace("_desc", ""),
            desc: columnSortName.includes("desc"),
          },
        ]
      : [],
  );

  // ----- Pagination state -----
  const [internalPagination, setInternalPagination] = useState<PaginationState>(
    offsetPagination ?? { pageIndex: 0, pageSize: defaultPageSize },
  );

  // Sync external pagination if provided
  useEffect(() => {
    if (offsetPagination && setPagination) {
      setInternalPagination(offsetPagination);
    }
  }, [offsetPagination, setPagination]);

  // Emit sort key to external store if requested
  useEffect(() => {
    if (handleTableSort && sorting[0]) {
      handleTableSort(`${sorting[0].id}_${sorting[0].desc ? "desc" : "asc"}`);
    } else if (handleTableSort && sorting.length === 0) {
      handleTableSort(null);
    }
  }, [handleTableSort, sorting]);

  // Scroll-to-top after sort without mutating table options during render
  // (React Compiler / v8 setOptions anti-pattern).
  const scrollToTopOnSortRef = useRef<() => void>(() => {});

  const handleSortingChange: OnChangeFn<SortingState> = (updater) => {
    setSorting(updater);
    scrollToTopOnSortRef.current();
  };

  // ----- Table instance -----
  // Subscribe only to the slices we drive; lean features already exclude unused
  // stock state. Controlled React state still re-renders this component on change.
  const table = useTable(
    {
      features: sellarTableFeatures,
      data,
      columns,
      manualSorting: !clientSort,
      manualPagination: !clientPagination,
      pageCount,
      state: {
        sorting,
        pagination: clientPagination ? internalPagination : offsetPagination,
      },
      onSortingChange: handleSortingChange,
      onPaginationChange: clientPagination
        ? setInternalPagination
        : setPagination,
    },
    (state) => ({
      sorting: state.sorting,
      pagination: state.pagination,
    }),
  );

  const { rows } = table.getRowModel();
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const tableRootRef = useRef<HTMLDivElement>(null);

  // SSR toggle
  const isServer = typeof window === "undefined";

  // ----- Vertical virtualization (contained-scroll client only) -----
  // TanStack Virtual mutates its instance; there is no compiler-safe hook.
  // oxlint-disable-next-line react/incompatible-library
  const rowVirtualizer = useVirtualizer({
    count: rows.length,
    estimateSize: () => 33,
    getScrollElement: () => tableContainerRef.current,
    // main-branch fix: better measuring (except Firefox)
    measureElement:
      !isServer &&
      typeof navigator !== "undefined" &&
      navigator.userAgent.indexOf("Firefox") === -1
        ? (el) => el?.getBoundingClientRect().height
        : undefined,
    overscan: 55,
    enabled: !isServer && useContainedScroll,
    initialOffset,
    onChange: (instance) => {
      if (!setScrollOffset || !useContainedScroll) return;
      const scrollOffsetUpper = (instance.scrollOffset ?? 0) + 20;
      const scrollOffsetLower = (instance.scrollOffset ?? 20) - 20;

      if (
        initialOffset > scrollOffsetUpper ||
        initialOffset < scrollOffsetLower
      ) {
        setScrollOffset(instance.scrollOffset ?? 0);
      }
    },
  });

  const scrollTableToTopRef = useRef<() => void>(() => {});
  scrollTableToTopRef.current = () => {
    // Always reset the table body — never scrollIntoView the page (that lands
    // under sticky app chrome and clips the first rows).
    tableContainerRef.current?.scrollTo({ top: 0, left: 0 });
    if (useContainedScroll && rows.length) {
      rowVirtualizer.scrollToIndex?.(0);
    }
  };

  scrollToTopOnSortRef.current = () => {
    if (!rows.length) return;
    scrollTableToTopRef.current();
  };

  const activePageIndex = clientPagination
    ? internalPagination.pageIndex
    : (offsetPagination?.pageIndex ?? 0);
  const activePageSize = clientPagination
    ? internalPagination.pageSize
    : (pagination?.pageSize ?? defaultPageSize);
  const paginationPageKey = pagination?.pageKey;

  // Skip the initial mount so URL-restored pagination doesn't yank scroll.
  const skipNextPaginationScrollRef = useRef(true);
  useEffect(() => {
    if (skipNextPaginationScrollRef.current) {
      skipNextPaginationScrollRef.current = false;
      return;
    }
    scrollTableToTopRef.current();
  }, [activePageIndex, activePageSize, paginationPageKey]);

  // Fit the table shell to the remaining viewport so chrome above + table ≈
  // one screen: the body is the only vertical scroller (no dual scrollbars).
  useLayoutEffect(() => {
    if (!useContainedScroll) return;
    const root = tableRootRef.current;
    if (!root) return;

    const fitToViewport = () => {
      const top = root.getBoundingClientRect().top;
      const available = Math.max(240, window.innerHeight - top - 8);
      root.style.maxHeight = `${available}px`;
    };

    fitToViewport();
    window.addEventListener("resize", fitToViewport);
    return () => {
      window.removeEventListener("resize", fitToViewport);
      root.style.maxHeight = "";
    };
  }, [useContainedScroll]);

  // ----- Horizontal virtualization (optional) -----
  const headerGroups = table.getHeaderGroups();
  const colCount = headerGroups[0]?.headers.length ?? 0;
  // oxlint-disable-next-line react/incompatible-library
  const colVirtualizer = useVirtualizer({
    horizontal: true,
    count: colCount,
    estimateSize: () => 100,
    getScrollElement: () => tableContainerRef.current,
    overscan: 25,
    enabled: horizontalInfinite,
  });

  // ----- Infinite scroll fetch more -----
  useEffect(() => {
    if (!infinite || !infiniteQuery?.fetchNextPage) return;
    const el = tableContainerRef.current;
    if (!el) return;
    const onScroll = () => {
      if (
        el.scrollHeight - el.scrollTop - el.clientHeight < 500 &&
        infiniteQuery.fetchNextPage &&
        !infiniteQuery.isFetching &&
        (infiniteQuery.data?.length ?? 0) <
          (infiniteQuery.totalCount ?? Infinity)
      ) {
        infiniteQuery.fetchNextPage();
      }
    };
    el.addEventListener("scroll", onScroll);
    return () => el.removeEventListener("scroll", onScroll);
  }, [infinite, infiniteQuery]);

  // ----- Client-side page count -----
  const calculatedPageCount =
    pageCount ??
    (clientPagination
      ? Math.ceil(
          (table.getPrePaginatedRowModel().rows.length ?? 0) /
            internalPagination.pageSize,
        )
      : offsetPagination && offsetPagination.pageSize
        ? Math.ceil((data?.length ?? 0) / offsetPagination.pageSize)
        : 1);

  // ----- Header click (asc/desc/none) -----
  const handleHeaderClick = (header: {
    column: {
      id: string;
      getCanSort: () => boolean;
    };
  }) => {
    if (!header.column.getCanSort()) return;
    setSorting((old = []) => {
      const current = old[0];
      if (!current || current.id !== header.column.id) {
        return [{ id: header.column.id, desc: false }];
      }
      if (!current.desc) {
        return [{ id: header.column.id, desc: true }];
      }
      return [];
    });
  };

  // ----- Render -----
  return (
    <TableContainer
      ref={tableRootRef}
      isPrinting={isPrinting}
      horizontalInfinite={horizontalInfinite}
      roundedBottom={roundedBottom}
      roundedTop={roundedTop}
      className={twMerge(
        "overflow-hidden",
        // Shell: body scrolls, pagination stays pinned under it.
        useContainedScroll && "flex min-h-0 flex-col",
      )}
    >
      <div
        ref={tableContainerRef}
        style={{
          overflow: isPrinting
            ? "visible"
            : useContainedScroll || horizontalInfinite
              ? "auto"
              : "visible",
          position: isPrinting ? "static" : "relative",
        }}
        className={twMerge(
          useContainedScroll && "min-h-0 flex-1",
          // Visible thin scrollbar so the body scroller is discoverable.
          (useContainedScroll || horizontalInfinite) &&
            [
              "[scrollbar-width:thin]",
              "[scrollbar-color:theme(colors.zinc.300)_transparent]",
              "dark:[scrollbar-color:theme(colors.zinc.600)_transparent]",
              "[&::-webkit-scrollbar]:h-2 [&::-webkit-scrollbar]:w-2",
              "[&::-webkit-scrollbar-track]:bg-transparent",
              "[&::-webkit-scrollbar-thumb]:rounded-full",
              "[&::-webkit-scrollbar-thumb]:bg-zinc-300",
              "dark:[&::-webkit-scrollbar-thumb]:bg-zinc-600",
            ].join(" "),
        )}
      >
        <TableLayout style={{ display: "grid" }}>
          <TableHeader
            style={
              isPrinting
                ? { display: "grid" }
                : {
                    display: "grid",
                    position: "sticky",
                    // Stick under the top of whichever ancestor actually scrolls
                    // (table container when contained, page panel otherwise).
                    top: 0,
                    zIndex: 1,
                  }
            }
          >
            {headerGroups.map((headerGroup) => (
              <TableHeaderRow
                key={headerGroup.id}
                className={twMerge(gridClasses)}
                style={{ ...gridStyles, display: "grid", width: "100%" }}
              >
                {headerGroup.headers.map((header, i) => (
                  <TableHeaderCell
                    key={header.id}
                    className={twMerge(
                      header.column.getCanSort() &&
                        "hover:bg-zinc-200 dark:hover:bg-zinc-800",
                      horizontalInfinite &&
                        i === 0 &&
                        "sticky top-0 left-0 z-20 min-w-[250px] bg-zinc-100 dark:bg-zinc-900",
                    )}
                    style={
                      horizontalInfinite && i > 0
                        ? {
                            minWidth: 170,
                            display: colVirtualizer
                              .getVirtualItems()
                              .some((v) => v.index === i)
                              ? undefined
                              : "none",
                          }
                        : undefined
                    }
                    onClick={() => handleHeaderClick(header)}
                  >
                    <div
                      className={
                        header.column.getCanSort()
                          ? "flex w-full cursor-pointer items-center select-none"
                          : "flex h-full w-full items-center"
                      }
                    >
                      {flexRender(
                        header.column.columnDef.header,
                        header.getContext(),
                      )}
                      {{
                        asc: (
                          <Icon
                            className="ml-auto inline-block"
                            icon="CHEVRON_UP"
                          />
                        ),
                        desc: (
                          <Icon
                            className="ml-auto inline-block"
                            icon="CHEVRON_DOWN"
                          />
                        ),
                      }[header.column.getIsSorted() as string] ??
                        (header.column.getCanSort() ? (
                          <Icon
                            className="ml-auto inline-block"
                            icon="UNFOLD"
                          />
                        ) : (
                          ""
                        ))}
                    </div>
                  </TableHeaderCell>
                ))}
              </TableHeaderRow>
            ))}
          </TableHeader>

          {/* Body: plain map (page-scroll / SSR) vs virtualized (contained) */}
          <TableBody
            style={
              isServer || !useContainedScroll
                ? undefined
                : {
                    display: "grid",
                    height: `${rowVirtualizer.getTotalSize()}px`,
                    position: "relative",
                  }
            }
          >
            {isServer || !useContainedScroll
              ? // --- Page-scroll / SSR: no vertical virtualization ---
                rows.map((row) => (
                  <TableRow
                    key={row.id}
                    className={twMerge(gridClasses)}
                    interactive={Boolean(onRowClick)}
                    onClick={(e) => {
                      const target = e.target as HTMLElement;
                      const isInteractive = target.closest(
                        'button, input, select, textarea, a, [role="button"], [data-interactive="true"]',
                      );
                      if (!isInteractive) {
                        onRowClick?.(row.original);
                      }
                    }}
                    style={{ ...gridStyles, width: "100%", display: "grid" }}
                  >
                    {row.getVisibleCells().map((cell, i) => (
                      <TableCell
                        key={cell.id}
                        padded={!horizontalInfinite || i !== 0}
                        className={twMerge(
                          // main-branch style: sticky first col when horizontalInfinite
                          horizontalInfinite
                            ? i === 0 &&
                                "sticky left-0 z-10 min-w-[250px] bg-zinc-50 dark:bg-zinc-900"
                            : undefined,
                        )}
                        style={
                          horizontalInfinite && i > 0
                            ? {
                                minWidth: 170,
                                display: colVirtualizer
                                  .getVirtualItems()
                                  .some((v) => v.index === i)
                                  ? undefined
                                  : "none",
                              }
                            : undefined
                        }
                      >
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext(),
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              : // --- Contained path: virtualized rows ---
                rowVirtualizer.getVirtualItems().map((vRow) => {
                  const row = rows[vRow.index];
                  if (!row) return null;
                  return (
                    <TableRow
                      key={row.id}
                      data-index={vRow.index}
                      ref={(node) => rowVirtualizer.measureElement(node)}
                      style={{
                        ...gridStyles,
                        position: "absolute",
                        transform: `translateY(${vRow.start}px)`,
                        width: "100%",
                        display: "grid",
                      }}
                      className={twMerge(gridClasses)}
                      interactive={Boolean(onRowClick)}
                      onClick={(e) => {
                        const target = e.target as HTMLElement;
                        const isInteractive = target.closest(
                          'button, input, select, textarea, a, [role="button"], [data-interactive="true"]',
                        );
                        if (!isInteractive) {
                          onRowClick?.(row.original);
                        }
                      }}
                    >
                      {row.getVisibleCells().map((cell, i) => (
                        <TableCell
                          key={cell.id}
                          padded={!horizontalInfinite || i !== 0}
                          className={twMerge(
                            // main-branch style: sticky first col when horizontalInfinite
                            horizontalInfinite
                              ? i === 0 &&
                                  "sticky left-0 z-10 min-w-[250px] bg-zinc-50 dark:bg-zinc-900"
                              : undefined,
                          )}
                          style={
                            horizontalInfinite && i > 0
                              ? {
                                  minWidth: 170,
                                  display: colVirtualizer
                                    .getVirtualItems()
                                    .some((v) => v.index === i)
                                    ? undefined
                                    : "none",
                                }
                              : undefined
                          }
                        >
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext(),
                          )}
                        </TableCell>
                      ))}
                    </TableRow>
                  );
                })}
          </TableBody>
        </TableLayout>
      </div>

      {data.length === 0 &&
        (infinite && infiniteQuery?.isFetching ? (
          <TableEmptyState>{loadingText}</TableEmptyState>
        ) : (
          <TableEmptyState>{noResultsText}</TableEmptyState>
        ))}

      {hasPagination && (
        <TablePaginationControls
          pagination={
            cursorPagination ?? {
              ...(offsetPagination?.mode === "offset-no-total"
                ? {
                    mode: "offset-no-total" as const,
                    hasNextPage: offsetPagination.hasNextPage,
                  }
                : { mode: "offset" as const, pageCount: calculatedPageCount }),
              pageIndex: activePageIndex,
              pageSize: activePageSize,
              isLoading: offsetPagination?.isLoading,
              onPageChange: (pageIndex) => {
                const update = clientPagination
                  ? setInternalPagination
                  : setPagination;
                update?.((previous) => ({ ...previous, pageIndex }));
              },
              onPageSizeChange: (pageSize) => {
                const update = clientPagination
                  ? setInternalPagination
                  : setPagination;
                update?.((previous) => ({ ...previous, pageSize }));
              },
            }
          }
          pageSizeOptions={pageSizeOptions}
          paginationLabel={paginationLabel}
        />
      )}
    </TableContainer>
  );
}
